import React, { HTMLAttributes } from 'react'
import classnames from 'classnames'

import Link from 'next/link'
import { FaYoutube } from 'react-icons/fa'

import styles from './styles.module.scss'
import { ArticleType } from 'types/article'
import ModalVideo from '@components/shared/modal-video'
import ReactPlayer from 'react-player'
import { ImageResponsive } from '@components/image-responsive'

interface ArticlePreviewProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  articlePath?: string
  articleType?: string
  imageUrl?: string
  videoUrl?: string
  alt?: string
  date?: string
  badge?: boolean
  title: string
  editorial?: string
  editorialPath?: string
  description?: string
  publishedAt?: string
  readingTime?: string
  direction?: 'left' | 'right' | 'top'
}

export const ArticlePreview = ({
  articlePath = '',
  articleType,
  date,
  title,
  direction = 'right',
  imageUrl,
  videoUrl,
  alt,
  badge,
  editorial,
  editorialPath,
  description,
  readingTime,
  publishedAt,
  className,
  ...rest
}: ArticlePreviewProps): JSX.Element => {
  const [showModal, setShowModal] = React.useState(false)

  return (
    <div
      className={classnames(
        className,
        { [styles.badge]: badge, [styles.video]: !!videoUrl },
        styles[direction],
        styles.container,
        ' !ps-0'
      )}
      {...rest}
    >
      {articleType === ArticleType.VIDEO && (
        <ModalVideo showModal={showModal} setShowModal={setShowModal}>
          <ReactPlayer playing width="100%" url={videoUrl} />
        </ModalVideo>
      )}
      {direction === 'top' && imageUrl && (
        <>
          {articleType === ArticleType.VIDEO ? (
            <div onClick={() => setShowModal(true)} className={styles.groupImg}>
              <div className={styles.wrapperVideoIcon}>
                <FaYoutube className={styles.videoIcon} />
              </div>
              <ImageResponsive
                src={imageUrl}
                alt={alt}
                className="rounded-lg"
              />
            </div>
          ) : (
            <Link href={articlePath} passHref>
              <ImageResponsive
                src={imageUrl}
                alt={alt}
                className="rounded-lg"
              />
            </Link>
          )}
        </>
      )}
      {editorial && (
        <span
          className={`bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2 ${
            direction === 'top' ? 'mt-1' : ''
          }`}
        >
          <Link href={editorialPath || editorial} passHref>
            {editorial}
          </Link>
        </span>
        // <div
        //   className={`${styles.editorial} ${direction === 'top' ? 'mt-1' : ''}`}
        // >
        //   {badge && <span className={styles.badge} />}
        //   {editorialPath ? (

        //   ) : (
        //     <a>{editorial}</a>
        //   )}
        // </div>
      )}

      <div className={classnames(styles.content, styles[direction])}>
        {direction === 'left' && imageUrl && (
          <>
            {articleType === ArticleType.ARTICLE && (
              <>
                {articlePath ? (
                  <Link
                    href={articlePath}
                    passHref
                    className={styles.containerImage}
                  >
                    <ImageResponsive
                      src={imageUrl}
                      alt={alt}
                      className="rounded-lg"
                    />
                  </Link>
                ) : (
                  <a className={styles.containerImage}>
                    <ImageResponsive
                      src={imageUrl}
                      alt={alt}
                      className="rounded-lg"
                    />
                  </a>
                )}
              </>
            )}

            {articleType === ArticleType.VIDEO && (
              <div
                onClick={() => setShowModal(true)}
                className={classnames([styles.groupImg, styles.containerImage])}
              >
                <div className={styles.wrapperVideoIcon}>
                  <FaYoutube className={styles.videoIcon} />
                </div>

                <ImageResponsive
                  src={imageUrl}
                  alt={alt}
                  className={styles.imageVideo + ' rounded-lg'}
                />
              </div>
            )}
          </>
        )}

        <div className={styles.information}>
          {articlePath ? (
            <>
              <h3>
                <Link href={articlePath} passHref className={styles.title}>
                  {title}
                </Link>
              </h3>
              {description && (
                <Link
                  href={articlePath}
                  passHref
                  className={styles.description + ' line-clamp-2 py-1'}
                >
                  {description}
                </Link>
              )}
            </>
          ) : (
            <>
              <p className={styles.title}>{title}</p>
              {description && (
                <p className={styles.description}>{description}</p>
              )}
            </>
          )}
        </div>

        {direction !== 'top' && imageUrl && (
          <>
            {articleType === ArticleType.ARTICLE && (
              <>
                {articlePath ? (
                  <Link
                    href={articlePath}
                    passHref
                    className={styles.containerImage}
                  >
                    <ImageResponsive
                      src={imageUrl}
                      alt={alt}
                      className="rounded-lg"
                    />
                  </Link>
                ) : (
                  <a className={styles.containerImage}>
                    <ImageResponsive
                      src={imageUrl}
                      alt={alt}
                      className="rounded-lg"
                    />
                  </a>
                )}
              </>
            )}

            {articleType === ArticleType.VIDEO && (
              <div
                onClick={() => setShowModal(true)}
                className={classnames([styles.groupImg, styles.containerImage])}
              >
                <div className={styles.wrapperVideoIcon}>
                  <FaYoutube className={styles.videoIcon} />
                </div>

                <ImageResponsive
                  src={imageUrl}
                  alt={alt}
                  className={styles.imageVideo + ' rounded-lg'}
                />
              </div>
            )}
          </>
        )}
      </div>

      <p
        className={
          classnames(styles.footer, {
            'flex-row': !date && readingTime
          }) + ' text-xs text-gray-500 flex items-center'
        }
      >
        {publishedAt && (
          <span>{new Date(publishedAt).toLocaleDateString('pt-BR') || ''}</span>
        )}
        {(date || readingTime) && (
          <span className="pe-4 ml-auto">{readingTime} min de leitura</span>
        )}
      </p>
    </div>
  )
}
